import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Clock, Copy, ExternalLink, Loader2, QrCode } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getPagamentosControllerStatusQueryKey,
  usePagamentosControllerAbrirComCupom,
  usePagamentosControllerIniciar,
  usePagamentosControllerStatus,
  usePagamentosControllerValidarCupom,
} from '@/api/generated/discussões-técnicas-pagamento/discussões-técnicas-pagamento';
import { useDiscussoesControllerEnviarMensagem } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import type { IniciarPagamentoDtoMetodo, PagamentoResponse, PreviaCupomResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { formatarCentavos } from '@/features/discussoes/rotulos';
import type { MensagemPendente } from '@/features/discussoes/tipos';
import { rotaDaDiscussao } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

/** 3s: rápido o bastante pra sentir o Pix cair sem martelar o servidor. */
const INTERVALO_POLLING_MS = 3000;

interface Props {
  discussaoId: string;
  /** Dúvidas/anexos escolhidos no wizard — só podem ser enviados quando o pagamento confirma. */
  mensagensPendentes?: MensagemPendente[];
}

/**
 * Passo 2: cupom de desconto opcional (docs/cupons-desconto-discussao.md do
 * backend), depois escolher Pix ou cartão, pagar, e aguardar a confirmação —
 * sempre `plataforma: 'WEB'`, porque esta é a etapa de pagamento do
 * navegador (o app mobile tem a própria, com o preço do App — e não tem
 * cupom por enquanto, ver §7 do doc).
 */
export function EtapaPagamento({ discussaoId, mensagensPendentes = [] }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [metodo, setMetodo] = useState<IniciarPagamentoDtoMetodo>('PIX');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [pagamento, setPagamento] = useState<PagamentoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pedeCpf, setPedeCpf] = useState(false);

  const [codigoCupom, setCodigoCupom] = useState('');
  const [cupom, setCupom] = useState<PreviaCupomResponse | null>(null);
  const [erroCupom, setErroCupom] = useState<string | null>(null);
  const [abertaPorCupom, setAbertaPorCupom] = useState(false);

  const iniciar = usePagamentosControllerIniciar();
  const validarCupom = usePagamentosControllerValidarCupom();
  const abrirComCupom = usePagamentosControllerAbrirComCupom();
  const enviarMensagem = useDiscussoesControllerEnviarMensagem();
  const mensagensEnviadas = useRef(false);

  const emAndamento = pagamento?.status === 'PENDENTE';
  const { data: statusAtual } = usePagamentosControllerStatus(discussaoId, {
    query: { enabled: emAndamento, refetchInterval: emAndamento ? INTERVALO_POLLING_MS : false },
  });

  useEffect(() => {
    if (statusAtual) setPagamento(statusAtual);
  }, [statusAtual]);

  useEffect(() => {
    if (pagamento?.status !== 'PAGO') return;
    toast.success('Pagamento confirmado! A discussão já está aberta.');
    enviarMensagensPendentesUmaVez();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamento?.status]);

  /** Compartilhado pelos dois desfechos "sem custo mais nenhum": PAGO e cupom de 100%. */
  function enviarMensagensPendentesUmaVez(): void {
    if (mensagensEnviadas.current || mensagensPendentes.length === 0) return;
    mensagensEnviadas.current = true;
    void Promise.all(
      mensagensPendentes.map((m) => enviarMensagem.mutateAsync({ id: discussaoId, data: m })),
    ).catch(() => {});
  }

  async function aoUsarCupom(): Promise<void> {
    setErroCupom(null);
    try {
      const previa = await validarCupom.mutateAsync({ id: discussaoId, data: { codigo: codigoCupom.trim() } });
      setCupom(previa);
    } catch (e) {
      setErroCupom(e instanceof ApiError ? e.message : 'Não foi possível conferir o cupom.');
    }
  }

  function aoRemoverCupom(): void {
    setCupom(null);
    setCodigoCupom('');
    setErroCupom(null);
  }

  async function aoFinalizarComCupom(): Promise<void> {
    if (!cupom) return;
    setErroCupom(null);
    try {
      await abrirComCupom.mutateAsync({ id: discussaoId, data: { codigo: cupom.codigo } });
      setAbertaPorCupom(true);
      toast.success('Discussão aberta! Já está com o advisor.');
      enviarMensagensPendentesUmaVez();
    } catch (e) {
      setErroCupom(e instanceof ApiError ? e.message : 'Não foi possível abrir a discussão com este cupom.');
    }
  }

  async function aoPagar() {
    setErro(null);
    try {
      const resposta = await iniciar.mutateAsync({
        id: discussaoId,
        data: {
          plataforma: 'WEB',
          metodo,
          ...(cpfCnpj.trim() ? { cpfCnpj: cpfCnpj.replace(/\D/g, '') } : {}),
          ...(cupom ? { codigoCupom: cupom.codigo } : {}),
        },
      });
      setPagamento(resposta);
    } catch (e) {
      if (e instanceof ApiError && e.codigo === 'CPF_CNPJ_OBRIGATORIO') {
        setPedeCpf(true);
        setErro(e.message);
        return;
      }
      setErro(e instanceof ApiError ? e.message : 'Não foi possível iniciar o pagamento.');
    }
  }

  if (pagamento?.status === 'PAGO' || abertaPorCupom) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-600" />
        <div>
          <p className="text-lg font-semibold text-foreground">
            {abertaPorCupom ? 'Discussão aberta' : 'Pagamento confirmado'}
          </p>
          <p className="text-sm text-muted-foreground">
            A discussão já está aberta para o advisor responder.
          </p>
        </div>
        <Button onClick={() => navigate(rotaDaDiscussao(discussaoId))}>Ir para a discussão</Button>
      </div>
    );
  }

  if (pagamento && (pagamento.status === 'PENDENTE' || pagamento.status === 'EXPIRADO')) {
    if (pagamento.status === 'EXPIRADO') {
      return (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <Clock className="w-16 h-16 text-muted-foreground" />
          <div>
            <p className="text-lg font-semibold text-foreground">Tempo expirado</p>
            <p className="text-sm text-muted-foreground">
              Você levou mais de 15 minutos para confirmar o pagamento e a cobrança venceu. O
              rascunho da discussão continua salvo — volte a ele para tentar de novo.
            </p>
          </div>
          <Button
            onClick={() => {
              // Sem isto, a query fica em cache com o EXPIRADO velho: a nova
              // montagem em `/discussoes/:id` (mesma queryKey) herdaria o
              // cache e cairia direto nesta MESMA tela, sem chance de tentar
              // de novo — um beco sem saída.
              queryClient.removeQueries({ queryKey: getPagamentosControllerStatusQueryKey(discussaoId) });
              setPagamento(null);
              navigate(rotaDaDiscussao(discussaoId));
            }}
          >
            Voltar para o rascunho salvo
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
          <span className="text-sm text-muted-foreground">Valor</span>
          <span className="font-semibold text-foreground">
            {pagamento.valorOriginalCentavos != null && (
              <span className="mr-2 text-xs font-normal text-muted-foreground line-through">
                {formatarCentavos(pagamento.valorOriginalCentavos)}
              </span>
            )}
            {formatarCentavos(pagamento.valorCentavos)}
          </span>
        </div>

        {pagamento.metodo === 'PIX' ? (
          <div className="flex flex-col items-center gap-3 text-center">
            {pagamento.pixQrCodeBase64 && (
              <img
                src={`data:image/png;base64,${pagamento.pixQrCodeBase64}`}
                alt="QR code do Pix"
                className="w-56 h-56 rounded-lg border border-brand/20"
              />
            )}
            {pagamento.pixPayload && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(pagamento.pixPayload ?? '');
                  toast.success('Código Pix copiado.');
                }}
              >
                <Copy className="w-4 h-4" />
                Copiar código Pix
              </Button>
            )}
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Aguardando confirmação do pagamento...
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            {pagamento.invoiceUrl && (
              <Button asChild>
                <a href={pagamento.invoiceUrl} target="_blank" rel="noreferrer">
                  Abrir checkout do cartão
                </a>
              </Button>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              Você será redirecionado para o Asaas, nosso parceiro de pagamentos, para concluir com
              segurança.
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Aguardando confirmação do pagamento...
            </p>
          </div>
        )}

        <Button type="button" variant="ghost" className="w-full" onClick={() => setPagamento(null)}>
          <ArrowLeft className="w-4 h-4" />
          Voltar e escolher outra forma de pagamento
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-brand">Cupom de desconto (opcional)</p>

        {cupom ? (
          <div className="rounded-lg border border-emerald-600/30 bg-emerald-600/10 px-4 py-3 space-y-1.5">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="text-sm font-semibold">
                Cupom {cupom.codigo} aplicado — {cupom.percentualDesconto}% de desconto
              </span>
            </div>
            {!cupom.aberturaDireta && (
              <p className="text-sm text-muted-foreground">
                De <span className="line-through">{formatarCentavos(cupom.valorOriginalCentavos)}</span> por{' '}
                <span className="font-semibold text-foreground">
                  {formatarCentavos(cupom.valorComDescontoCentavos)}
                </span>
              </p>
            )}
            <button
              type="button"
              onClick={aoRemoverCupom}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Trocar ou remover cupom
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              value={codigoCupom}
              onChange={(e) => setCodigoCupom(e.target.value)}
              placeholder="Digite o código"
              className={CLASSE_CONTROLE}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void aoUsarCupom()}
              disabled={!codigoCupom.trim() || validarCupom.isPending}
            >
              {validarCupom.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Usar cupom
            </Button>
          </div>
        )}

        {erroCupom && <p className="text-sm text-destructive">{erroCupom}</p>}
      </div>

      {cupom?.aberturaDireta ? (
        <Button onClick={() => void aoFinalizarComCupom()} disabled={abrirComCupom.isPending} className="w-full">
          {abrirComCupom.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Finalizar cadastro da discussão
        </Button>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-sm font-medium text-brand">Forma de pagamento</p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={metodo === 'PIX' ? 'default' : 'outline'}
                onClick={() => setMetodo('PIX')}
                className="flex-1"
              >
                <QrCode className="w-4 h-4" />
                Pix
              </Button>
              <Button
                type="button"
                variant={metodo === 'CARTAO_CREDITO' ? 'default' : 'outline'}
                onClick={() => setMetodo('CARTAO_CREDITO')}
                className="flex-1"
              >
                Cartão de crédito
              </Button>
            </div>
          </div>

          {pedeCpf && (
            <Campo id="cpfCnpj" rotulo="CPF ou CNPJ" obrigatorio dica="Só na primeira cobrança.">
              <Input
                id="cpfCnpj"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(e.target.value)}
                placeholder="Só números"
                className={CLASSE_CONTROLE}
              />
            </Campo>
          )}

          <Button onClick={() => void aoPagar()} disabled={iniciar.isPending} className="w-full">
            {iniciar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Pagar
          </Button>
        </>
      )}
    </div>
  );
}
